<template>
  <div class="p-3 pm-tab-shell h-100">
    <!-- Toolbar -->
    <div v-if="viewMode === 'active'" class="pm-tab-toolbar d-flex justify-content-end mb-3">
      <button class="btn btn-sm btn-outline-secondary mb-0" style="font-size:0.8rem;" @click="openCreate">
        <i class="fas fa-plus me-1"></i> Create Sub Project
      </button>
    </div>

    <!-- Table -->
    <div class="pm-tab-table-wrap">
      <div v-if="loading" class="text-center py-5 d-flex align-items-center justify-content-center flex-grow-1">
        <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
        <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
      </div>
      <div v-else class="sp-table pm-tab-table">
        <div class="sp-table-header pm-tab-table-header">
          <span class="col-name">SUB PROJECT</span>
          <span class="col-date text-center">LAUNCH DATE</span>
          <span class="col-date text-center">DUE DATE</span>
          <span class="col-team text-center">TEAM</span>
          <span class="col-pm text-center">PM / PO</span>
          <span class="col-status text-center">STATUS</span>
          <span v-if="viewMode === 'active'" class="col-action text-center">ACTION</span>
        </div>
        <div class="sp-table-body pm-tab-table-body">
        <div v-if="subProjects.length === 0" class="pm-empty-table-row">
          <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
          <p class="text-secondary text-sm mt-2 mb-0">
            {{ viewMode === 'history' ? 'No deployed sub projects yet.' : 'No sub projects yet. Click "Create Sub Project" to add one.' }}
          </p>
        </div>
        <div v-for="sub in subProjects" :key="sub.subProjectId" class="sp-row">
          <div class="col-name">
            <p class="sp-name mb-0 sp-name-link" @click="openScope(sub)">{{ sub.subProjectName }}</p>
            <p class="sp-sub mb-0">{{ sub.projectName || '—' }}</p>
          </div>
          <div class="col-date text-center">
            <span class="text-sm text-secondary">{{ formatDate(sub.launchDate) }}</span>
          </div>
          <div class="col-date text-center">
            <span class="text-sm text-secondary">{{ formatDate(sub.dueDate) }}</span>
          </div>
          <div class="col-team text-center">
            <span class="text-sm text-secondary">{{ teamLabel(sub.teamTrimmed || sub.team) }}</span>
          </div>
          <div class="col-pm text-center">
            <span v-if="sub.ownerFullName" class="owner-badge bo-assignee-pill">{{ formatAssignedName(sub.ownerFullName) }}</span>
            <span v-else class="text-sm text-secondary">—</span>
          </div>
          <div class="col-status text-center">
            <span class="status-pill" :class="statusPillClass(sub.subProjectStatus)">
              {{ statusLabel(sub.subProjectStatus) }}
            </span>
          </div>
          <div v-if="viewMode === 'active'" class="col-action text-center">
            <button class="btn btn-link btn-sm p-0 action-edit me-2" title="Edit" @click="openEdit(sub)">
              <i class="fas fa-edit" style="font-size:0.85rem;"></i>
            </button>
            <button class="btn btn-link btn-sm p-0 action-delete" title="Delete" @click="remove(sub)">
              <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Modal: Create / Edit Sub Project -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay">
        <div class="modal-box" style="width:520px;">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ editId ? 'Edit Sub Project' : 'Create Sub Project' }}</h6>
          </div>
          <div class="p-3">
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Parent Project <span class="text-danger">*</span></label>
              <base-select
                size="sm"
                v-model="form.projectId"
                :options="projectSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select project —"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Sub Project Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control form-control-sm" v-model="form.name" placeholder="Enter sub project name" />
            </div>
            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Launch Date</label>
                <input type="date" class="form-control form-control-sm" v-model="form.launchDate" />
              </div>
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Due Date</label>
                <input type="date" class="form-control form-control-sm" v-model="form.dueDate" />
              </div>
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Jira Space</label>
              <base-select
                size="sm"
                v-model="form.jiraProjectKey"
                :options="jiraProjectSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select Space —"
                @change="onJiraProjectChange"
              />
              <span v-if="jiraLoadingProjects" class="text-xs text-secondary mt-1 d-block">
                <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces...
              </span>
            </div>
            <div class="mb-3" v-if="form.jiraProjectKey">
              <label class="text-xs font-weight-bold mb-1 d-block">Jira Tickets <span class="text-secondary font-weight-normal">(optional)</span></label>
              <!-- Selected tags -->
              <div class="d-flex flex-wrap gap-1 mb-2" v-if="form.jiraIssueKeys.length > 0">
                <span v-for="key in form.jiraIssueKeys" :key="key" class="jira-tag">
                  <i class="fab fa-jira me-1"></i>{{ key }}
                  <button type="button" class="jira-tag-remove" @click="removeJiraTicket(key)">×</button>
                </span>
              </div>
              <!-- Search -->
              <input type="text" class="form-control form-control-sm mb-1" v-model="jiraSearch"
                placeholder="Search ticket..." @input="onJiraSearch" />
              <!-- Issue list -->
              <div class="jira-issue-list">
                <div v-if="jiraLoadingIssues" class="text-center py-2 text-secondary text-xs">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                </div>
                <div v-else-if="jiraIssues.length === 0" class="text-center py-2 text-secondary text-xs">No tickets found</div>
                <div v-for="i in jiraIssues" :key="i.key"
                  class="jira-issue-item"
                  :class="{ 'jira-issue-selected': form.jiraIssueKeys.includes(i.key) }"
                  @click="toggleJiraTicket(i.key)">
                  <i class="fas fa-check-circle me-1 text-success" v-if="form.jiraIssueKeys.includes(i.key)"></i>
                  <i class="fas fa-circle me-1" style="font-size:0.5rem; color:transparent;" v-else></i>
                  <span class="text-xs font-weight-bold me-1">{{ i.key }}</span>
                  <span class="text-xs">{{ i.summary }}</span>
                  <span v-if="i.status" class="text-xs text-secondary ms-1">({{ i.status }})</span>
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">PM / PO</label>
              <base-select
                size="sm"
                v-model="form.projectOwner"
                :options="userSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select user —"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Team Owner</label>
              <base-select
                size="sm"
                v-model="form.team"
                :options="teamSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select team —"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
              <base-select
                size="sm"
                v-model="form.status"
                :options="statusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select status —"
              />
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <div v-if="saveError" class="me-auto text-danger text-xs align-self-center">{{ saveError }}</div>
            <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeModal">Cancel</button>
            <button class="btn btn-sm bg-gradient-dark text-white mb-0" @click="save">Save</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Scope Modal (PM — shared ScopeTab, no AI) -->
    <Teleport to="body">
      <div v-if="showScopeModal && scopeSub" class="modal-overlay">
        <div class="modal-box pm-scope-modal">
          <div class="d-flex align-items-center justify-content-between p-3 border-bottom">
            <div>
              <h6 class="mb-0 font-weight-bold">{{ scopeSub.subProjectName }}</h6>
              <span class="text-xs text-secondary">{{ scopeSub.projectName || '' }}</span>
            </div>
            <button type="button" class="btn btn-link btn-sm p-0 text-secondary" title="Close" @click="closeScopeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="pm-scope-modal-body">
            <scope-tab
              :key="`scope-${scopeSub.subProjectId}-${viewMode}`"
              :sub-project-id="scopeSub.subProjectId"
              :sub-project-name="scopeSub.subProjectName || ''"
              :initial-jira-links="scopeInitialJiraLinks"
              :enable-ai="false"
              :read-only="viewMode === 'history'"
              embedded
            />
          </div>
          <div class="d-flex justify-content-end p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeScopeModal">Close</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { API_BASE_URL, fetchWithTimeout } from "@/config/apiBase";
import { projectsApi, subProjectsApi, usersApi } from "@/services/backendApi";
import ScopeTab from "@/views/components/tabs/ScopeTab.vue";
import { encodeJiraLinksPayload, parseJiraLinksPayload } from "@/utils/jiraLinksPayload";
import {
  PLAN_MANAGEMENT_STATUS_OPTIONS,
  statusLabel,
  statusPillClass,
  normalizeSubProjectStatus,
} from "@/utils/subProjectStatus";

export default {
  name: "sub-projects-table",
  components: { ScopeTab },
  props: {
    viewMode: {
      type: String,
      default: "active",
      validator: (v) => ["active", "history"].includes(v),
    },
  },
  data() {
    return {
      loading: false,
      showModal: false,
      editId: null,
      saveError: null,
      form: { projectId: "", name: "", launchDate: "", dueDate: "", jiraLink: "", jiraProjectKey: "", jiraIssueKeys: [], projectOwner: null, team: "", status: "to_do" },
      subProjects: [],
      projects: [],
      users: [],
      jiraProjects: [],
      jiraIssues: [],
      jiraLoadingProjects: false,
      jiraLoadingIssues: false,
      jiraSearch: "",
      jiraSearchTimer: null,
      showScopeModal: false,
      scopeSub: null,
      scopeInitialJiraLinks: [],
    };
  },
  watch: {
    viewMode: {
      immediate: false,
      handler() {
        this.loadSubProjects();
      },
    },
  },
  async created() {
    await Promise.all([this.loadProjects(), this.loadSubProjects(), this.loadUsers()]);
  },
  computed: {
    statusOptions() {
      const base = [...PLAN_MANAGEMENT_STATUS_OPTIONS];
      const cur = normalizeSubProjectStatus(this.form.status);
      if (cur && !base.some((o) => o.value === cur)) {
        base.push({ value: cur, label: statusLabel(cur) });
      }
      return base;
    },
    projectSelectOptions() {
      return (this.projects || []).map((p) => ({
        label: p.projectName,
        value: Number(p.projectId),
      }));
    },
    jiraProjectSelectOptions() {
      return (this.jiraProjects || []).map((p) => ({
        label: `${p.name} (${p.key})`,
        value: p.key,
      }));
    },
    userSelectOptions() {
      return (this.users || []).map((u) => ({
        label: this.formatUserDisplayName(u),
        value: Number(u.userId),
      }));
    },
    teamSelectOptions() {
      return [
        { label: "Team A", value: "a" },
        { label: "Team B", value: "b" },
        { label: "ALL", value: "all" },
      ];
    },
  },
  methods: {
    formatUserDisplayName(user) {
      const first = String(user?.firstName || "").trim();
      const nick = String(user?.nickname || "").trim();
      if (!first) return "—";
      return nick ? `${first} (${nick})` : first;
    },
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName;
    },
    async loadProjects() {
      try { this.projects = await projectsApi.getAll(); } catch (e) { console.error(e); }
    },
    async loadUsers() {
      try { this.users = await usersApi.getAll(); } catch (e) { console.error(e); }
    },
    async loadSubProjects() {
      this.loading = true;
      try {
        const params = this.viewMode === "active" ? { excludeDeployed: "true" } : {};
        const data = await subProjectsApi.getAll(params);
        const filtered = (data || []).filter((s) => {
          const st = normalizeSubProjectStatus(s.subProjectStatus);
          if (this.viewMode === "history") return st === "deployed";
          return st !== "deployed";
        });
        this.subProjects = filtered.sort((a, b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate) - new Date(b.dueDate);
        });
      } catch (e) { console.error("loadSubProjects error:", e); }
      finally { this.loading = false; }
    },
    openCreate() {
      this.editId = null;
      this.jiraSearch = "";
      this.jiraIssues = [];
      this.form = { projectId: "", name: "", launchDate: "", dueDate: "", jiraLink: "", jiraProjectKey: "", jiraIssueKeys: [], projectOwner: null, team: "", status: "to_do" };
      this.showModal = true;
      this.loadJiraProjects();
    },
    openEdit(sub) {
      this.editId = sub.subProjectId;
      this.jiraSearch = "";
      this.jiraIssues = [];
      const existing = parseJiraLinksPayload(sub.projectLink);
      const jiraIssueKeys = existing.links.map((l) => l.key);
      const jiraProjectKey = existing.projectKey || (jiraIssueKeys[0] ? jiraIssueKeys[0].split("-")[0] : "");
      this.form = {
        projectId: Number(sub.projectId),
        name: sub.subProjectName,
        launchDate: sub.launchDate ? String(sub.launchDate).substring(0, 10) : "",
        dueDate: sub.dueDate ? String(sub.dueDate).substring(0, 10) : "",
        jiraLink: sub.projectLink || "",
        jiraProjectKey,
        jiraIssueKeys,
        projectOwner: sub.projectOwner ? Number(sub.projectOwner) : null,
        team: (sub.teamTrimmed || sub.team || "").trim(),
        status: normalizeSubProjectStatus(sub.subProjectStatus) || "to_do",
      };
      this.showModal = true;
      this.loadJiraProjects();
      if (jiraProjectKey) this.loadJiraIssues(jiraProjectKey);
    },
    closeModal() { this.showModal = false; this.saveError = null; },
    formatDate(val) {
      if (!val) return "—";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      return `${d}-${m}-${y}`;
    },
    teamLabel(val) {
      const v = (val || "").trim().toLowerCase();
      return { a: "Team A", b: "Team B", all: "ALL" }[v] || "—";
    },
    statusLabel,
    statusPillClass,
    async save() {
      if (!this.form.name.trim() || !this.form.projectId) return;
      this.saveError = null;
      try {
        const base = "https://lightnet-thailand.atlassian.net/browse";
        const jiraUrl = encodeJiraLinksPayload(
          this.form.jiraProjectKey,
          this.form.jiraIssueKeys.map((k) => ({ key: k, url: `${base}/${k}` })),
        );
        const data = {
          projectId: this.form.projectId,
          name: this.form.name.trim(),
          launchDate: this.form.launchDate || null,
          dueDate: this.form.dueDate || null,
          projectLink: jiraUrl,
          projectOwner: this.form.projectOwner || null,
          team: this.form.team || null,
          status: normalizeSubProjectStatus(this.form.status) || "to_do",
        };
        if (this.editId) await subProjectsApi.update(this.editId, data);
        else await subProjectsApi.insert(data);
        await this.loadSubProjects();
        this.closeModal();
      } catch (e) {
        console.error("save sub project error:", e);
        this.saveError = e.message || "Save failed";
      }
    },
    async loadJiraProjects() {
      if (this.jiraProjects.length > 0) return;
      this.jiraLoadingProjects = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/projects`);
        this.jiraProjects = await res.json();
      } catch (e) { console.error("loadJiraProjects error:", e); }
      finally { this.jiraLoadingProjects = false; }
    },
    async loadJiraIssues(projectKey, search = "") {
      this.jiraLoadingIssues = true;
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${projectKey}${q}`);
        this.jiraIssues = await res.json();
      } catch (e) { console.error("loadJiraIssues error:", e); }
      finally { this.jiraLoadingIssues = false; }
    },
    onJiraProjectChange() {
      this.form.jiraIssueKeys = [];
      this.jiraIssues = [];
      this.jiraSearch = "";
      if (this.form.jiraProjectKey) this.loadJiraIssues(this.form.jiraProjectKey);
    },
    toggleJiraTicket(key) {
      const idx = this.form.jiraIssueKeys.indexOf(key);
      if (idx === -1) this.form.jiraIssueKeys.push(key);
      else this.form.jiraIssueKeys.splice(idx, 1);
    },
    removeJiraTicket(key) {
      this.form.jiraIssueKeys = this.form.jiraIssueKeys.filter(k => k !== key);
    },
    onJiraSearch() {
      clearTimeout(this.jiraSearchTimer);
      this.jiraSearchTimer = setTimeout(() => {
        if (this.form.jiraProjectKey) this.loadJiraIssues(this.form.jiraProjectKey, this.jiraSearch);
      }, 400);
    },
    async remove(sub) {
      if (!confirm(`Delete "${sub.subProjectName}"?`)) return;
      try {
        await subProjectsApi.delete(sub.subProjectId);
        await this.loadSubProjects();
      } catch (e) {
        console.error("delete sub project error:", e);
        alert(e.message || "Delete failed");
      }
    },
    openScope(sub) {
      const fromProject = parseJiraLinksPayload(sub.projectLink);
      this.scopeSub = sub;
      this.scopeInitialJiraLinks = fromProject.links || [];
      this.showScopeModal = true;
    },
    closeScopeModal() {
      this.showScopeModal = false;
      this.scopeSub = null;
      this.scopeInitialJiraLinks = [];
    },
  },
};
</script>

<style scoped>
.sp-table.pm-tab-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
}
.sp-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sp-row {
  display: flex;
  align-items: center;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}
.sp-row:last-child { border-bottom: none; }
.sp-row:hover { background: #fafbfc; }

/* Column widths */
.col-name   { flex: 3; min-width: 0; }
.col-date   { flex: 1.1; min-width: 85px; }
.col-team   { flex: 0.9; min-width: 70px; }
.col-pm     { flex: 1.5; min-width: 100px; }
.col-status { flex: 1.2; min-width: 90px; }
.col-action { flex: 0.8; min-width: 65px; }

.sp-name { font-size: 0.875rem; font-weight: 700; color: #344767; }
.sp-name-link { cursor: pointer; color: #4361ee; text-decoration: underline dotted; }
.sp-name-link:hover { color: #2d4bc8; }
.sp-sub  { font-size: 0.78rem; color: #8392ab; margin-top: 2px; }
.owner-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.04rem 0.26rem;
  line-height: 1.05;
}

.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}

/* Jira link pill */
.jira-link-pill {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 7px;
  text-decoration: none;
  white-space: nowrap;
}
.jira-link-pill:hover { background: #d1ecf1; color: #0a6c87; }

/* Action buttons */
.action-edit   { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}

/* Jira ticket picker */
.jira-tag {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  margin: 2px 2px 2px 0;
}
.jira-tag-remove {
  background: none;
  border: none;
  color: #0c7a9a;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 0 4px;
  line-height: 1;
}
.jira-issue-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.jira-issue-item {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}
.jira-issue-item:last-child { border-bottom: none; }
.jira-issue-item:hover { background: #f8f9fa; }
.jira-issue-selected { background: #e8f4fd; }

.jira-chip {
  background: #e8f4fd;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  padding: 4px 12px;
  font-size: 0.78rem;
}
.jira-chip-remove {
  cursor: pointer;
  color: #8392ab;
  font-size: 1rem;
  line-height: 1;
  margin-left: 2px;
}
.jira-chip-remove:hover { color: #f5365c; }
.jira-links-table th,
.jira-links-table td { vertical-align: middle; }
.jira-id-cell { width: 124px; padding-right: 0.35rem !important; }
.jira-title-col { width: auto; }
.jira-title-cell { padding-left: 0.2rem !important; padding-right: 0.35rem !important; }
.jira-status-col { width: 170px; }
.jira-status-cell { padding-left: 0.25rem !important; padding-right: 0.25rem !important; }
.jira-action-col { width: 48px; }
.jira-action-cell { text-align: center; padding-left: 0 !important; padding-right: 0 !important; }
.action-delete { color: #e74c3c; line-height: 1; }
.action-delete:hover { color: #c0392b; }
.jira-status-readonly {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  background: #e9ecef;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #495057;
}
</style>

<style>
.pm-scope-modal {
  width: 960px;
  max-width: 96vw;
  height: 85vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.pm-scope-modal-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
